import { TableBody, TableCell, TableContainer, TableHead } from '@mui/material';
import { createStyles, withStyles } from '@mui/styles';
import lodash from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Guid } from 'guid-typescript';

// Services
import { packageApi } from '@/main/api';
import { PackageTypeRequest } from '@/main/models/request';
import { convertDecimalToString, convertStringToDecimalPercentage, getTranslateByKey, isEmpty, isNullUndefined, removeEmptyValues } from '@/main/services/utils';
import { calculateVolume } from '@/main/services/utilsNumber';
import { getPackage, usePackageReducer } from '@/main/store/package';

// Components
import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import Box from '@/main/components/shared/box';
import {
  BaseTableRowModalSide,
  KOGTableRowModalSide,
  StyledTable
} from '@/main/components/shared/boxGrid/styles';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogCheckbox from '@/main/components/shared/checkbox/kogCheckbox';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { PackageTypeResponse } from '@/main/models/response';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';


// Styles
import { IHandleAddCategoryToGrid } from '../../packWorkflowScreen';
import * as S from './styles';
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';
import KogInputPercentageControlled from '@/main/components/shared/inputPercentage/inputPercentage';
import { convertStringForBoolean } from '@/main/services/utilsString';
import RenderTooltipMessage from '@/main/components/shared/renderTooltipMessage/renderTooltipMessage';
import { KeyValueDefault } from '@/main/models/common';

const INITIAL_FORM_STATE = {
  categoryName: '' as string,
  isHollow: false as boolean,
  percentPacking: '' as string,
  height: '' as string,
  width: '' as string,
  depth: '' as string,
  exceptions: null as string[] | null
};

type IModalPackWorkflowForm = typeof INITIAL_FORM_STATE;

const StyledTableCell = withStyles(() =>
  createStyles({
    head: {
      color: '#6b6b6b !important',
      fontWeight: 'bold'
    },
    body: {
      fontSize: 14,
      color: '#000'
    }
  })
)(TableCell);

interface IModalPackWorkflow {
  guidCountry: string;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectPackageTypeForEdit?: string | null;
  handleAddCategoryToGrid: ({
    data,
    guidPackageType,
    isDelete
  }: IHandleAddCategoryToGrid) => void;
  packageTypeData: PackageTypeResponse[] | null;
}

function ModalPackWorkflow({
  guidCountry,
  setIsOpenModal,
  selectPackageTypeForEdit = null,
  handleAddCategoryToGrid,
  packageTypeData
}: IModalPackWorkflow) {
  const dispatch = useDispatch();
  const { package: packageReducer } = usePackageReducer();
  const [dataExceptions, setDataExceptions] = useState<string[]>([]);
  const [addSameCategory, setAddSameCategory] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [volume, setVolume] = useState<number | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, submitCount },
    reset,
    trigger,
    watch: watchForm
  } = useForm<IModalPackWorkflowForm>({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: INITIAL_FORM_STATE
  });

  const watcherValueInputHeight = watchForm('height');
  const watcherValueInputWidth = watchForm('width');
  const watcherValueInputDepth = watchForm('depth');
  const watcherHollowCheckbox = watchForm('isHollow');

  function hasDuplicateCategoryName(
    data: PackageTypeResponse[],
    categoryName: string,
    guid: string | null = Guid.EMPTY
  ) {
    const VerifyExistName = data.some(
      item =>
        item.name.toLowerCase() === categoryName.toLowerCase() &&
        item.guid != guid
    );
    return VerifyExistName;
  }

  function onSubmit(formInputs: IModalPackWorkflowForm) {
    if (
      packageTypeData &&
      hasDuplicateCategoryName(
        packageTypeData,
        formInputs.categoryName,
        selectPackageTypeForEdit
      )
    ) {
      toast.error(
        getTranslateByKey(
          'information.aCategoryWithThisNameAlreadyExists',
          true
        )
      );
      return;
    }

    const request: PackageTypeRequest = {
      guid: String(selectPackageTypeForEdit),
      name: formInputs.categoryName,
      height: Number(formInputs.height),
      width: Number(formInputs.width),
      depth: Number(formInputs.depth),
      guidCountry,
      guidsPackageTypeException: dataExceptions,
      addNewCategory: addSameCategory,
      hollow: formInputs.isHollow,
      hollowPercentage: convertStringToDecimalPercentage(formInputs.percentPacking)
    };

    if (selectPackageTypeForEdit) {
      packageApi
        .update(removeEmptyValues(request))
        .then(res => {
          const resData = res.data.result;
          handleAddCategoryToGrid({
            data: resData,
            guidPackageType: selectPackageTypeForEdit,
            isDelete: false
          });
          toast.success(
            getTranslateByKey('information.categorySavedSuccessfully')
          );
        })
        .catch(() => { })
        .finally(() => setIsOpenModal(false));
    } else {
      packageApi
        .save(removeEmptyValues(request))
        .then(res => {
          const resData = res.data.result;
          handleAddCategoryToGrid({ data: resData });
          toast.success(
            getTranslateByKey('information.categorySavedSuccessfully')
          );
        })
        .catch(() => { })
        .finally(() => {
          reset(INITIAL_FORM_STATE);
          setIsOpenModal(false);
        });
    }
  }

  function handleCancel() {
    reset(INITIAL_FORM_STATE);
    setIsOpenModal(false);
  }

  function handleToggleCheckbox(guid: string) {
    const cloneOptionsSelected = lodash.cloneDeep(dataExceptions);
    const index = cloneOptionsSelected?.indexOf(guid);

    if (index > -1) {
      cloneOptionsSelected?.splice(index, 1);
    } else {
      cloneOptionsSelected?.push(guid);
    }
    setDataExceptions(cloneOptionsSelected);
  }

  function handleToggleCheckboxAdd() {
    setAddSameCategory(true);
  }

  function handleToggleCheckboxHollow(value: boolean) {
    const guidsPackagesHollow = packageReducer.data?.filter(
      item => convertStringForBoolean(item.default)
    ).map(item => String(item.key));

    if (!value) {
      setValue("percentPacking", INITIAL_FORM_STATE.percentPacking);
      clearErrors("percentPacking");
      setDataExceptions(prev => prev.filter(item => !guidsPackagesHollow?.includes(item)));

      if (!isEdit) {
        setAddSameCategory(false);
      }

      return;
    }

    if (isNullUndefined(guidsPackagesHollow)) return;

    setDataExceptions(prev => [...prev, ...guidsPackagesHollow]);
    if (!isEdit) {
      setAddSameCategory(true);
    }
  }

  const rowHeadersModal = [getTranslateByKey('information.exceptions'), ''];

  useEffect(() => {
    if (selectPackageTypeForEdit) {
      setIsEdit(true);
      setIsLoading(true);
      packageApi
        .getByGuidPackageType(selectPackageTypeForEdit)
        .then(res => {
          const resData = res.data.result;
          const formattedHollowPercentage = isEmpty(resData.hollowPercentage) ? '' : convertDecimalToString(resData.hollowPercentage);

          setValue('categoryName', resData.name);
          setValue('depth', String(resData.depth));
          setValue('height', String(resData.height));
          setValue('width', String(resData.width));
          setValue('isHollow', resData.hollow);
          setValue('percentPacking', formattedHollowPercentage);

          resData.packageTypeExceptionResponse.map(item => {
            return setDataExceptions(prev => [...prev, item.guid]);
          });
        })
        .catch(() => {
          reset(INITIAL_FORM_STATE);
          setIsOpenModal(false);
        })
        .finally(() => setIsLoading(false));
    }

    const requestDomain = removeEmptyValues({
      guidCountry
    });

    dispatch(getPackage(requestDomain));
  }, []);

  useEffect(() => {
    if (
      watcherValueInputHeight &&
      watcherValueInputWidth &&
      watcherValueInputDepth
    ) {
      setVolume(
        calculateVolume(
          Number(watcherValueInputHeight),
          Number(watcherValueInputWidth),
          Number(watcherValueInputDepth)
        )
      );
    } else {
      setVolume(null);
    }
  }, [watcherValueInputHeight, watcherValueInputWidth, watcherValueInputDepth]);

  useEffect(() => {
    if (submitCount > 0) {
      trigger('percentPacking');
    }
  }, [watcherHollowCheckbox])

  if (isLoading) return <LoadingComponent />;

  const disabledExceptionHollow = (group: KeyValueDefault) => watcherHollowCheckbox && (convertStringForBoolean(group.default) || selectPackageTypeForEdit === group.key);
  const messageNotPossibleToSetException = getTranslateByKey("information.notPosibleExceptionHollow");

  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <ColumnLabel
            label={
              <S.ColumnTitle>
                {getTranslateByKey('information.registerCategory')}
              </S.ColumnTitle>
            }
            content={
              <S.InputGroup>
                <KogInputTextControlledNew
                  label={getTranslateByKey('information.categoryName')}
                  helperText={
                    errors.categoryName?.type === 'required'
                      ? getTranslateByKey(
                        'information.categoryNameRequired',
                        true
                      )
                      : ''
                  }
                  name="categoryName"
                  error={!!errors.categoryName}
                  controllerProps={{ rules: { required: true } }}
                  control={control}
                  id="id-modalPackWorkflow-txt-categoryName"
                />

                <S.AreaHollow>
                  <KogCheckBoxControlled
                    id='id-modalPackWorkflow-checkbox-isHollow'
                    label={`${getTranslateByKey("information.hollow")}?`}
                    tooltipMessage={getTranslateByKey('information.tooltipHollow')}
                    nameCustom='isHollow'
                    removeMargin
                    control={control}
                    onChangeCustom={handleToggleCheckboxHollow}
                  />

                  <KogInputPercentageControlled
                    idScreen='id-modalPackWorkflow'
                    label={getTranslateByKey('information.consideredPacking')}
                    name='percentPacking'
                    required={watcherHollowCheckbox}
                    control={control}
                    customInputProps={{
                      disabled: !watcherHollowCheckbox,
                    }}
                    configRange={{
                      rangeNumbers: {
                        min: 1,
                        max: 100
                      }
                    }}
                  />
                </S.AreaHollow>

                {volume && (
                  <S.LabelVolume>
                    {getTranslateByKey('information.volume')}:{' '}
                    <span>{volume}L</span>
                  </S.LabelVolume>
                )}
              </S.InputGroup>
            }
          />

          <ColumnLabel
            label={
              <S.ColumnTitle>
                {getTranslateByKey('partdetail.dimensions')}
              </S.ColumnTitle>
            }
            content={
              <S.InputGroup>
                <KogInputTextControlledNew
                  label={getTranslateByKey('information.height')}
                  helperText={
                    errors.height?.type === 'required'
                      ? getTranslateByKey('information.heightNotInformed', true)
                      : ''
                  }
                  name="height"
                  hasOnlyNumberInteger
                  error={!!errors.height}
                  controllerProps={{ rules: { required: true } }}
                  control={control}
                  id="id-modalPackWorkflow-txt-height"
                />
                <KogInputTextControlledNew
                  label={getTranslateByKey('information.width')}
                  helperText={
                    errors.width?.type === 'required'
                      ? getTranslateByKey('information.widthNotInformed', true)
                      : ''
                  }
                  name="width"
                  hasOnlyNumberInteger
                  error={!!errors.width}
                  controllerProps={{ rules: { required: true } }}
                  control={control}
                  id="id-modalPackWorkflow-txt-width"
                />

                <KogInputTextControlledNew
                  label={getTranslateByKey('information.depth')}
                  helperText={
                    errors.depth?.type === 'required'
                      ? getTranslateByKey('information.depthNotInformed', true)
                      : ''
                  }
                  name="depth"
                  hasOnlyNumberInteger
                  error={!!errors.depth}
                  controllerProps={{ rules: { required: true } }}
                  control={control}
                  id="id-modalPackWorkflow-txt-depth"
                />
              </S.InputGroup>
            }
          />
          <ColumnLabel
            label={
              <S.ColumnTitle>
                {getTranslateByKey('information.exceptionsCategory')}
              </S.ColumnTitle>
            }
            content={
              packageReducer.isLoading ? (
                <LoadingComponent />
              ) : (
                packageReducer.data && (
                  <S.BoxTable>
                    {!isEdit && (
                      <S.AreaSelectSameException>
                        <KogCheckbox
                          disabled={disabledExceptionHollow({ key: '', value: '', default: 'True' })}
                          tooltipMessage={disabledExceptionHollow({ key: '', value: '', default: 'True' }) ? messageNotPossibleToSetException : ''}
                          checked={addSameCategory}
                          onChange={() => {
                            handleToggleCheckboxAdd();
                          }}
                        />
                        <S.TextScreen>
                          {getTranslateByKey('information.addCreatedPackageTypeToList')}
                        </S.TextScreen>
                      </S.AreaSelectSameException>
                    )}
                    <TableContainer>
                      <StyledTable
                        $sizeCell="small"
                        $typeGrid="oddEven"
                        aria-label="customized table"
                        style={{
                          minWidth: '100%'
                        }}>
                        <TableHead>
                          <BaseTableRowModalSide>
                            {rowHeadersModal &&
                              rowHeadersModal.map((header, index) => (
                                <StyledTableCell key={index}>
                                  {header}
                                </StyledTableCell>
                              ))}
                          </BaseTableRowModalSide>
                        </TableHead>
                        <TableBody id="id-modalPackWorkflow-controls-checkbox">
                          {packageReducer.data &&
                            packageReducer.data?.map(group => (
                              <KOGTableRowModalSide key={group.key}>
                                <TableCell width="90%">
                                  <S.TextScreen $isDisabled={disabledExceptionHollow(group)}>{group.value}</S.TextScreen>
                                </TableCell>
                                <TableCell width="10%">
                                  <KogCheckbox
                                    value={group.key}
                                    tooltipMessage={disabledExceptionHollow(group) ? messageNotPossibleToSetException : ''}
                                    checked={dataExceptions.includes(String(group.key))}
                                    disabled={disabledExceptionHollow(group)}
                                    onChange={() => {
                                      handleToggleCheckbox(String(group.key));
                                    }}
                                  />
                                </TableCell>
                              </KOGTableRowModalSide>
                            ))}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </S.BoxTable>
                )
              )
            }
          />

          <S.AreaButton>
            <SecondaryButton onClick={handleCancel}>
              {getTranslateByKey('information.cancel')}
            </SecondaryButton>
            <Button type="submit">
              {getTranslateByKey('information.save')}
            </Button>
          </S.AreaButton>
        </form>
      </Box >
    </>
  );
}

export default ModalPackWorkflow;
